import React from 'react';

// Third-party imports
import { useLocation } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Button from '../ui/Button';

// Utility imports
import { logEvent } from '../../utils/analytics';
import { smoothScrollTo } from '../../utils/scrollUtils';

/**
 * Props for the NavLink component
 */
interface NavLinkProps {
  /** The URL or hash to navigate to */
  href: string;
  /** Whether this is an external link */
  external?: boolean;
  /** Click handler function */
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  /** Content to display inside the link */
  children: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Whether to style as a button */
  isButton?: boolean;
  /** Accessible label for the link */
  ariaLabel?: string;
}

/**
 * NavLink component for both internal hash navigation and external links
 * Supports smooth scrolling for hash links and opens external links in new tab
 * Uses the transition effect for page navigation
 * Now supports button styling with isButton prop
 * Enhanced with proper accessibility attributes
 */
const NavLink: React.FC<NavLinkProps> = ({ 
  href, 
  external = false, 
  onClick, 
  children, 
  className = "", 
  isButton = false,
  ariaLabel
}) => {
  const location = useLocation();
  
  // Base classes for regular links
  const linkClasses = "nav-link text-md flex items-center gap-1 focus:outline-none focus-visible:outline-accent";
  
  // Special classes for button styling with improved states
  const buttonClasses = "px-4 py-2 rounded-full bg-accent text-primary hover:bg-accent/90 active:bg-accent/70 focus:ring-2 focus:ring-accent/50 focus:ring-offset-2 focus:ring-offset-background transition-all duration-200 font-medium";
  
  // Combine the appropriate classes
  const finalClasses = `${isButton ? buttonClasses : linkClasses} ${className}`;
  
  // Simple classes without animation effects
  let isScrolling = false;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    // Safely prevent default only if event exists
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // Track the navigation click event
    if (external) {
      logEvent('navigation', 'external_link_click', href);
    } else if (href.startsWith('#')) {
      logEvent('navigation', 'anchor_link_click', href);
    } else {
      logEvent('navigation', 'internal_navigation', href);
    }

    // Call onClick if provided
    if (onClick) {
      onClick(e);
      // onClick will handle the navigation, so return early
      return;
    }

    // Handle navigation logic
    if (!external && href.startsWith('#')) {
      if (location.pathname !== '/') {
        // If we're not on the homepage, navigate to homepage with hash
        window.location.href = '/' + href;
      } else {
        // If already on homepage, scroll to the section using smoothScrollTo
        const elementId = href.slice(1);

        // Don't proceed if we're already scrolling
        if (isScrolling) return;
        
        // Set the scrolling flag
        isScrolling = true;
        
        // Use the smoothScrollTo utility for consistent scrolling behavior
        smoothScrollTo(elementId);
        
        // Release the scrolling lock after animation completes
        setTimeout(() => {
          isScrolling = false;
        }, 1000); // Lock scrolling for 1 second (typical scroll animation duration)
      }
    } else if (external) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      // Use the transition system for internal page navigation
      if (window.navigateWithTransition) {
        window.navigateWithTransition(href);
      } else {
        // Fallback if transition system isn't ready
        window.location.href = href;
      }
    }
  };

  // Determine the current path for active state
  const currentPath = location.pathname;
  const isActive = 
    currentPath === href || 
    (href === '/' && currentPath === '') || 
    (currentPath.startsWith(href) && href !== '/' && !href.startsWith('#'));

  if (isButton) {
    return (
      <Button
        href={href}
        external={external}
        onClick={(e) => handleClick(e as React.MouseEvent<HTMLAnchorElement>)}
        className={className}
        variant="primary"
        size="lg"
        aria-label={ariaLabel}
      >
        {children}
        {external && (
          <span className="sr-only">(opens in a new tab)</span>
        )}
      </Button>
    );
  }

  return (
    <a 
      href={href}
      onClick={handleClick}
      className={finalClasses}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      aria-label={ariaLabel || undefined}
      aria-current={isActive ? "page" : undefined}
    >
      <div className="flex items-center gap-1">
        {children}
        {external && (
          <ArrowUpRight className="icon-sm" aria-hidden="true" />
        )}
      </div>
      {external && (
        <span className="sr-only">(opens in a new tab)</span>
      )}
    </a>
  );
};

export default NavLink;

